import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import {
  DirectVisitorDetailDialogProfileFragment,
  FollowupMessageEventPatientDetailFragment,
  FollowupQuestionnaireSheetEventOnPatientDetailDialogFragment,
  GetMedixsPatientEventsDocument,
  GetPatientEventsDocument,
  GuestPatientMedicationFollowupCommentFragment,
  LineVisitorDetailDialogProfileFragment,
  PatientDetailDialogProfileFragment,
  PatientMedicationFollowupCommentFragment,
  TracingReportPatientDetailFragment,
  WebVisitorDetailDialogProfileFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { eventPageInfoState } from '~/state/partials/patient_detail_dialog/atoms';
import { groupBy } from '~/utils/group_by';
import { Label } from '~/utils/label';

type Patient =
  | DirectVisitorDetailDialogProfileFragment
  | WebVisitorDetailDialogProfileFragment
  | PatientDetailDialogProfileFragment
  | LineVisitorDetailDialogProfileFragment;

type Event =
  | GuestPatientMedicationFollowupCommentFragment
  | PatientMedicationFollowupCommentFragment
  | FollowupMessageEventPatientDetailFragment
  | FollowupQuestionnaireSheetEventOnPatientDetailDialogFragment
  | TracingReportPatientDetailFragment;

export const useFetchEvents = (patient?: Patient) => {
  const [{ isResetEvent, page, perPage }, setEventPageInfoState] = useAtom(eventPageInfoState);

  const [medicationFollowupEvent, clearMedicationFollowupEvent] = useSubscriptions(
    'MedicationFollowupQuestionnaireSheet',
    ['created'],
  );

  const [events, setEvents] = useState<Event[]>([]);
  const [getEvents, { data, loading }] = useLazyQuery(GetPatientEventsDocument, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
  });

  const [getMedixsPatientEvents] = useLazyQuery(GetMedixsPatientEventsDocument, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only',
  });

  const guestPatientId = patient?.__typename !== 'Patient' ? patient?.id : undefined;
  const patientId = patient?.__typename === 'Patient' ? patient.id : undefined;
  const medixsPatient = patient?.medixsPatient;

  const totalPage = data?.events?.pagesCount || 1;
  const hasNextPage = data?.events?.hasNextPage || false;

  const dateGroups = groupBy(events, (event) => {
    if (event.__typename === 'MedicationFollowupMessage') {
      return Label.YYYYMMDDja(event.sendAt);
    } else if (
      event.__typename === 'MedicationFollowupQuestionnaireSheet' ||
      event.__typename === 'PatientMedicationFollowupComment' ||
      event.__typename === 'GuestPatientMedicationFollowupComment' ||
      event.__typename === 'TracingReport'
    ) {
      return Label.YYYYMMDDja(event.createdAt);
    }
  });

  const eventsWithDate = dateGroups
    .map((group) => {
      const date = group[0];
      const events = group[1];
      return events.map((event, idx) => {
        return {
          event,
          date: events.length === idx + 1 ? date : null,
        };
      });
    })
    .flat();

  const appendEvents = useCallback((nodes: Event[] | undefined, currentPage: number) => {
    if (!nodes?.length) return;
    setEvents((prev) => {
      if (!((prev.length === 0 && currentPage === 1) || (prev.length > 0 && currentPage > 1))) {
        return prev;
      }
      return [...prev, ...nodes];
    });
  }, []);

  const fetchAndAppend = useCallback(
    async (targetPage: number, targetPerPage: number) => {
      if (medixsPatient) {
        const { data: result } = await getMedixsPatientEvents({
          variables: {
            medixsPatientId: medixsPatient.id,
            page: targetPage,
            perPage: targetPerPage,
          },
        });
        appendEvents(getNode(result, 'MedixsPatient')?.events?.nodes, targetPage);
      } else {
        const { data: result } = await getEvents({
          variables: {
            guestPatientId,
            patientId,
            page: targetPage,
            perPage: targetPerPage,
          },
        });
        appendEvents(result?.events?.nodes, targetPage);
      }
    },
    [medixsPatient, getMedixsPatientEvents, getEvents, guestPatientId, patientId, appendEvents],
  );

  useEffect(() => {
    const fetchEvents = async () => {
      await fetchAndAppend(page, perPage);
      setEventPageInfoState((_state) => ({ ..._state, isResetEvent: false }));
    };

    fetchEvents();
  }, [
    page,
    perPage,
    clearMedicationFollowupEvent,
    isResetEvent,
    setEventPageInfoState,
    fetchAndAppend,
  ]);

  useEffect(() => {
    if (isResetEvent) {
      // TODO: リセットしたいタイミングを正しくしないと、無駄に処理が走りそうなので後に修正する
      setEvents([]);
    }
  }, [isResetEvent]);

  useEffect(() => {
    if (!medicationFollowupEvent) {
      return;
    }

    if (['created', 'updated'].includes(medicationFollowupEvent.actionName)) {
      setEvents([]);
      fetchAndAppend(1, 20);
    }
  }, [medicationFollowupEvent, fetchAndAppend]);

  return {
    loading,
    events,
    totalPage,
    hasNextPage,
    eventsWithDate,
  };
};
